<template>
    <div
        class="bonusomat-quest-card"
        :class="{ 'bonusomat-quest-card--active': isActive }"
        @click="openQuestDetailClick(actionsEnabled, quest.id, quest.title, 'questDetail', $event)"
    >
        <div class="bonusomat-quest-card__image-wrapper">
            <DsBadge v-if="quest.badge" :text="quest.badge" class="bonusomat-quest-card__badge" />
            <WebpImage css-class="bonusomat-quest-card__image" :src="quest.image" alt="" />
        </div>
        <h3
            class="bonusomat-quest-card__title"
            :class="{ 'bonusomat-quest-card__title--calculated-height': useCalculatedTitleHeight }"
            v-html="quest.title"
        ></h3>
        <div class="bonusomat-quest-card__info">
            <div class="bonusomat-quest-card__info-item" v-if="isActive">
                <BonusomatQuestAwardIcon :icon-name="quest.awardIcon" />
                {{ quest.award }}
            </div>
            <div class="bonusomat-quest-card__info-item" v-if="hasTasksAndIsDone">
                <DsIcon name="bet-list" :width="16" :height="16" />
                {{ tasksCountLabel }}
            </div>
            <div class="bonusomat-quest-card__info-item" v-if="hasTasksAndIsInProgress">
                <BonusomatProgress :key="progressLoadCounter" :progress="questProgress" />
                {{ completedTasksLabel }}
            </div>
            <div class="bonusomat-quest-card__info-item">
                <DsIcon name="time" :width="16" :height="16" />
                {{ questTimeLabel }}
            </div>
        </div>
        <div class="bonusomat-quest-card__footer">
            <template v-if="isActive">
                <div class="bonusomat-quest-card__footer-info bonusomat-quest-card__footer-info--active">
                    <BonusomatProgress :key="progressLoadCounter" :progress="questProgress" show-percentage-number />
                    <div class="bonusomat-quest-card__footer-texts">
                        <span>Splněno</span>
                        <strong>{{ completedTasks }}</strong>
                    </div>
                </div>
                <div class="bonusomat-quest-card__footer-actions">
                    <DsButton
                        theme="dark"
                        size="sm"
                        type="interaction"
                        icon-left="bet-list"
                        @click="actionsEnabled && openQuestDetail(quest.id)"
                    >
                        <span class="bonusomat-quest-card__show-tasks-text">Zobrazit</span> Úkoly
                    </DsButton>
                    <DsButton
                        theme="light"
                        size="sm"
                        type="error"
                        icon-left="cross"
                        @click.stop="actionsEnabled && deactivateQuest('small')"
                    />
                </div>
            </template>
            <template v-else-if="isCompleted">
                <div class="bonusomat-quest-card__footer-info bonusomat-quest-card__footer-info--green">
                    <BonusomatProgress :progress="100" is-completed />
                    Splněno
                </div>
                <div class="bonusomat-quest-card__footer-actions">
                    <DsButton
                        theme="dark"
                        size="sm"
                        type="interaction"
                        @click.stop="openQuestDetailClick(actionsEnabled, quest.id, quest.title, 'detailButton')"
                        >Detail</DsButton
                    >
                </div>
            </template>
            <template v-else>
                <div class="bonusomat-quest-card__footer-info bonusomat-quest-card__footer-info--yellow">
                    <BonusomatQuestAwardIcon :icon-name="quest.awardIcon" />
                    <div class="bonusomat-quest-card__award-text-wrap">
                        <div class="bonusomat-quest-card__award-text">{{ quest.award }}</div>
                    </div>
                </div>
                <div class="bonusomat-quest-card__footer-actions">
                    <DsButton
                        theme="dark"
                        size="sm"
                        type="interaction"
                        @click.stop="openQuestDetailClick(actionsEnabled, quest.id, quest.title, 'detailButton')"
                        >Detail</DsButton
                    >
                    <DsButton
                        theme="dark"
                        size="sm"
                        :is-disabled="isOnLogoutPage || !isPlayable"
                        @click.stop="actionsEnabled && activateQuestClick(quest.id, quest.title)"
                        >{{ quest.totalTasks > 0 ? "Hrát" : "Získat bonus" }}</DsButton
                    >
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, toRef } from "vue";

import DsBadge from "@sazka/design-system/src/badge/ds-badge.vue";
import WebpImage from "@sazka/design-system/src/image/webp-image.vue";
import DsIcon from "@sazka/design-system/src/icon/ds-icon.vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import { BonusomatDatalayer } from "@/casino/bonusomat/utils/analytics/bonusomat-datalayer";
import BonusomatProgress from "../shared/bonusomat-progress.vue";
import BonusomatQuestAwardIcon from "./bonusomat-quest-award-icon.vue";

import useBonusomatData from "../../composables/use-bonusomat-data";
import { useQuestDetails } from "../../composables/use-quest-details";

import { BonusomatQuest } from "../../models/bonusomat-quest";
import { BonusomatLogoutQuest } from "../../models/bonusomat-logout-quest";
import { useLoyaltyPlayerStatus } from "@/loyalty/composables/use-loyalty-player-status";

const props = defineProps<{
    quest: BonusomatQuest | BonusomatLogoutQuest;
    progressLoadCounter?: string;
    actionsDisabled?: boolean;
    isOnLogoutPage?: boolean;
    useCalculatedTitleHeight?: boolean;
}>();

const emit = defineEmits(["detailClicked"]);

const questRef = toRef(props, "quest");

const { bonusomatPlayerStatus } = useLoyaltyPlayerStatus();
const { bonusomatPlayerData, remainingSpinsCount } = bonusomatPlayerStatus;

const { openQuestDetail, activateQuest, deactivateQuest } = useBonusomatData();
const { isActive, isCompleted, isPlayable, tasksCountLabel, completedTasksLabel, questTimeLabel, questProgress } =
    useQuestDetails(questRef, props.isOnLogoutPage);

const actionsEnabled = computed(() => !props.actionsDisabled);

const completedTasks = computed(() => {
    const totalTasks = props.quest.totalTasks;

    switch (totalTasks) {
        case 1:
            return `${props.quest.completedTasks} z ${totalTasks} úkolu`;
        case 2:
        case 3:
        case 4:
            return `${props.quest.completedTasks} ze ${totalTasks} úkolů`;
        default:
            return `${props.quest.completedTasks} z ${totalTasks} úkolů`;
    }
});

const hasTasksAndIsDone = computed(
    () =>
        isActive.value ||
        (props.quest.totalTasks > 0 &&
            (props.quest.completedTasks === 0 || props.quest.completedTasks === props.quest.totalTasks))
);

const hasTasksAndIsInProgress = computed(
    () =>
        !isActive.value &&
        props.quest.totalTasks > 0 &&
        props.quest.completedTasks > 0 &&
        props.quest.completedTasks < props.quest.totalTasks
);

const activateQuestClick = (questId: number, questName: string) => {
    activateQuest(questId);
    BonusomatDatalayer.pushStartQuest(
        questName,
        "hryLobby",
        bonusomatPlayerData.value.points,
        remainingSpinsCount.value
    );
};

const openQuestDetailClick = (
    actionsEnabled: boolean,
    questId: number,
    questName: string,
    clickSource: string,
    event?: Event
) => {
    // In Safari even the click.stop event on disabled button is still propagated to parent card element => ignore these clicks
    if ((event?.target as HTMLElement)?.className == "button__inner") {
        return;
    }

    emit("detailClicked");

    if (actionsEnabled) {
        openQuestDetail(questId);
        BonusomatDatalayer.pushOpenQuest(
            questName,
            clickSource,
            bonusomatPlayerData.value.points,
            remainingSpinsCount.value
        );
    }
};
</script>
